import React, { useState } from 'react'
import { Button, Col, Row } from 'react-bootstrap'
import styled from 'styled-components'
import { Form, Formik } from 'formik'

import { useDispatch, useSelector } from 'react-redux'
import { _ } from 'utils'
import { $gray_900 } from 'utils/colors'
import { useEventSlug } from 'hooks'

import { Loader } from 'components'
import Switch from 'features/forms/components/Switch'
import { eventPreviewSelector } from 'features/auth/eventPreviewSlice'
import EditableTextField from '../FormFields/EditableTextField'
import EditableFileUpload, { DOCUMENTS_ONLY } from '../FormFields/EditableFileUpload'
import { BoothConfigModal, DocumentData } from '../types'
import useUpload from '../hooks/useUpload'
import { updateDocument } from '../actions'
import { fetchBooth, selectBoothSlug } from '../fetchBoothSlice'

const S = {
    Container: styled.div`
        display: block;
        padding: 30px 0;
    `,
    Title: styled.h2`
        color: ${$gray_900};
        font-weight: bold;
        margin: auto auto 30px 0;
        padding: 0 0 0 0;
    `,
    Body: styled.div`
        display: block;
    `,
    FormActions: styled.div`
        display: flex;
        align-items: center;
        justify-content: space-between;
    `,
    Switch: styled(Switch)`
        width: fit-content;
        margin-bottom: 0!important;
    `
}

const DocumentModal = ({ close, data }: BoothConfigModal) => {
    const document = data as DocumentData
    const isEditing = !!document
    const upload = useUpload('document')
    const dispatch = useDispatch()
    const eventSlug = useEventSlug()
    const boothSlug = useSelector(selectBoothSlug)
    const eventPreview = useSelector(eventPreviewSelector)

    const [isSaving, setSaving] = useState(false)
    const [name, setName] = useState<string>(document?.name)
    const [newFile, setNetFile] = useState<File>(null)
    const validForm = name && (newFile || isEditing)

    const onFileSelect = (file: File) => {
        setName(file?.name || '')
        setNetFile(file)
    }

    const update = (supportTranslate?: boolean, isNative?: boolean) => {
        if (isEditing) {
            return dispatch(updateDocument({
                id: document.id,
                data: { name, support_translate: supportTranslate, is_native: isNative },
                eventSlug,
                boothSlug,
            }))
        }

        return upload(newFile, name, supportTranslate, isNative)
    }

    const buttonUpdateClickHandler = async (values) => {
        setSaving(true)
        await update(values?.supportTranslate, values?.isNative)
        await dispatch(fetchBooth({ eventSlug, boothSlug }))
        close?.()
    }

    return (
        <S.Container>
            <S.Body className="container">
                <Row>
                    <Col md={12}>
                        { (newFile || isEditing) ? (
                            <EditableTextField
                                defaultValue={name}
                                inputType="text"
                                label={_('booth.config.documents.item.name')}
                                onUpdate={setName}
                                tapToEdit={false}
                            />
                        ) : (
                            <EditableFileUpload
                                accept={DOCUMENTS_ONLY}
                                onFileSelect={onFileSelect}
                            />
                        )}
                    </Col>
                </Row>

                <Row>
                    {
                        isSaving
                            ? <Loader />
                            : (eventPreview?.enable_page_translation ? (
                                <Col md={12}>
                                    <Formik
                                        initialValues={{
                                            supportTranslate: document?.support_translate,
                                            isNative: document?.is_native
                                        }}
                                        onSubmit={buttonUpdateClickHandler}
                                    >
                                        <Form>
                                            <S.FormActions>
                                                <div className="flex">
                                                    <Button
                                                        variant="primary mt-2 mr-2"
                                                        type="submit"
                                                        disabled={!validForm}
                                                    >
                                                        {_('form.save')}
                                                    </Button>
                                                    <Button
                                                        variant="default mt-2"
                                                        onClick={close}
                                                    >
                                                        {_('form.cancel')}
                                                    </Button>
                                                </div>
                                                <S.Switch
                                                    name="isNative"
                                                    label={_('booths.form.is_native_pdf')}
                                                    helperText={
                                                        _('booths.form.is_native_pdf_helper_text')
                                                    }
                                                />
                                                <S.Switch
                                                    name="supportTranslate"
                                                    label={_('booths.form.enable_translate_document')}
                                                    helperText={
                                                        _('booths.form.enable_translate_document_helper_text')
                                                    }
                                                />
                                            </S.FormActions>
                                        </Form>
                                    </Formik>
                                </Col>
                            ) : (
                                <Col md={12}>
                                    <Button
                                        variant="primary mt-2 mr-2"
                                        onClick={buttonUpdateClickHandler}
                                        disabled={!validForm}
                                    >
                                        {_('form.save')}
                                    </Button>
                                    <Button
                                        variant="default mt-2"
                                        onClick={close}
                                    >
                                        {_('form.cancel')}
                                    </Button>
                                </Col>
                            )
                            )
                    }
                </Row>
            </S.Body>
        </S.Container>
    )
}

export default DocumentModal
